import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { buildUserIndex, toUserSummary, type UserIndex } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { Page, UserListItem } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'

/**
 * El equipo, para los selectores de responsable y para poner nombres.
 *
 * `GET /users` viene paginado (`Page[UserRead]`: `items` + `has_more`, sin
 * `total` ni `page`) porque es el mismo endpoint que alimenta
 * `/settings/users`. Acá se desenvuelve y se recorta a `UserSummary`.
 *
 * Además de los selectores, esta query es lo que permite poner un nombre donde
 * el backend manda un id: las listas de proyectos y tareas traen
 * `internal_owner_id` y `assignee_id` pelados, nunca el objeto.
 */
/**
 * De dónde salen los nombres, según quién mira.
 *
 * `GET /users` es **de admin**: a un `tech_member` le contesta 403. Cuando el
 * índice se armaba sólo con esa ruta, el suyo quedaba **vacío** —no
 * "incompleto", vacío— y la consecuencia se veía en toda la aplicación: la
 * columna RESPONSABLE de `/companies` decía "Sin responsable" para las cuatro
 * empresas, las tarjetas de proyecto no tenían dueño y los avatares no tenían
 * iniciales. Todo por unos 403 que el front se tragaba en silencio.
 *
 * `GET /users/assignable` sí lo puede pedir cualquier interno y trae justo lo
 * que hace falta para poner nombres en sus propias pantallas.
 *
 * Se elige por rol y no probando y cayendo en el catch a propósito: el fallback
 * funcionaba, pero dejaba un 403 en la consola **en cada carga de página**, y
 * "cero errores de consola" es una de las pocas señales que quedan para notar
 * que algo se rompió de verdad. Gastarla en un error que ya sabemos que va a
 * pasar es perderla.
 */
function rutaDeNombres(client: QueryClient): string {
  const me = client.getQueryData<{ is_admin?: boolean }>(qk.auth.me())
  return me?.is_admin ? 'users?limit=200' : 'users/assignable?limit=200'
}

function usersQueryPara(client: QueryClient) {
  return {
    queryKey: qk.settings.users(),
    queryFn: async () => {
      const page = await api.get<Page<UserListItem>>(rutaDeNombres(client))
      return page.items.map(toUserSummary)
    },
    staleTime: 10 * 60_000,
  }
}

/**
 * El equipo, para poner nombres donde el backend manda un id.
 *
 * Los selectores de responsable **no** usan esto: usan `useAssignableUsers`,
 * que pregunta lo que de verdad importa ahí —a quién se le puede asignar— y lo
 * contesta el servidor con la misma consulta que valida el guardado.
 */
export function useUsers() {
  const client = useQueryClient()
  return useQuery(usersQueryPara(client))
}

/**
 * El índice `id -> usuario`, garantizando que esté ANTES de mapear una lista.
 *
 * `ensureQueryData` es lo que evita la carrera: si las tareas llegaran primero,
 * se cachearían con `assignee: null` y el nombre no aparecería hasta el
 * siguiente refetch —un bug que se ve una vez cada diez cargas y no se
 * reproduce—. Si ya está en cache (10 minutos), no cuesta ningún request.
 */
export async function ensureUserIndex(client: QueryClient): Promise<UserIndex> {
  try {
    return buildUserIndex(await client.ensureQueryData(usersQueryPara(client)))
  } catch {
    // Que no haya nombres no es razón para que no haya tareas.
    return buildUserIndex([])
  }
}

/** Versión de hook, para los componentes que solo pintan. */
export function useUserIndex(): UserIndex {
  const { data } = useUsers()
  return buildUserIndex(data ?? [])
}

export function useEnsureUserIndex(): () => Promise<UserIndex> {
  const client = useQueryClient()
  return () => ensureUserIndex(client)
}

/**
 * A quién le puedo pasar trabajo. **`GET /users/assignable`, no `GET /users`.**
 *
 * Es la fuente de los selectores de responsable, y la diferencia con `useUsers`
 * no es cosmética: `GET /users` es de admin y devuelve a TODO el mundo,
 * incluidos los contactos de cliente. Usarlo para asignar producía las dos
 * mitades del mismo bug, y el QA funcional encontró las dos:
 *
 * * Al **admin** le ofrecía clientes, que el `PATCH` después rechaza con un 422.
 *   Un desplegable que ofrece opciones que el servidor no acepta.
 * * Al **`tech_member`** le devolvía 403, el front se lo tragaba en silencio y
 *   el desplegable quedaba con una sola opción: "Sin responsable". Tres de los
 *   cuatro asientos internos no podían asignarle una tarea a nadie.
 *
 * Con `projectId` se acota a la empresa de ese proyecto —el caso de la ficha de
 * una tarea—; sin él, a la unión de las empresas del principal, que es el caso
 * del filtro del portafolio. La regla la decide el servidor con la **misma
 * consulta** que valida el `assignee_id` al guardar, así que lo que se ofrece y
 * lo que se acepta no pueden separarse.
 */
export function useAssignableUsers(projectId?: string | null) {
  return useQuery({
    queryKey: qk.settings.users({ assignable: true, projectId: projectId ?? null }),
    queryFn: async () => {
      const query = projectId
        ? `?project_id=${encodeURIComponent(projectId)}&limit=200`
        : '?limit=200'
      const page = await api.get<Page<UserListItem>>(`users/assignable${query}`)
      return page.items.map(toUserSummary)
    },
    staleTime: 5 * 60_000,
  })
}
